import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import apiRequest from "../api";
import logo from "../images/muyaz.png";

export default function Navbar() {
  const auth = useAuth();
  const user = auth?.user;
  const logout = auth?.logout;

  const [menuOpen, setMenuOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [status, setStatus] = useState({ error: "", success: "" });
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const navRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    if (logout) {
      await logout();
      navigate("/login");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setStatus({ error: "", success: "" });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return setStatus({ error: "New passwords do not match", success: "" });
    }

    try {
      setLoading(true);
      const response = await apiRequest.put(
        "/users/change-password",
        passwordForm
      );
      const result = response.data;

      if (result && result.success) {
        setStatus({
          error: "",
          success: result.message || "Password updated!",
        });
        setTimeout(() => {
          setIsPasswordModalOpen(false);
          setPasswordForm({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setStatus({ error: "", success: "" });
        }, 2000);
      } else {
        throw new Error(result.message || "Failed to update password");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Server Error";
      setStatus({ success: "", error: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <nav
      ref={navRef}
      className="bg-white text-slate-800 border-b border-gray-100 shadow-sm relative z-[100]"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
        {/* Logo and Name - ALWAYS VISIBLE */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src={logo}
            alt="Logo"
            className="w-12 h-12 rounded-full object-cover shadow-sm flex-shrink-0 border border-gray-100"
          />
          <span className="text-xl font-black tracking-tight text-teal-700 whitespace-nowrap">
            MUYAZ
          </span>
        </Link>

        {/* Auth-Dependent Section */}
        {user ? (
          <>
            {/* Mobile Button */}
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={
                    menuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M4 6h16M4 12h16M4 18h16"
                  }
                />
              </svg>
            </button>

            {/* Navigation Menu */}
            <ul
              className={`absolute md:static top-full left-0 w-full md:w-auto bg-white md:bg-transparent flex flex-col md:flex-row md:items-center gap-2 md:gap-8 px-6 md:px-0 py-4 md:py-0 transition-all border-b md:border-none shadow-xl md:shadow-none ${
                menuOpen ? "flex" : "hidden md:flex"
              }`}
            >
              <li>
                <Link
                  to="/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 hover:text-teal-600 font-medium"
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link
                  to="/send-sms"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 hover:text-teal-600 font-medium"
                >
                  Send SMS
                </Link>
              </li>
              <li>
                <Link
                  to="/report"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 hover:text-teal-600 font-medium"
                >
                  SMS Report
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    setIsPasswordModalOpen(true);
                    setMenuOpen(false);
                  }}
                  className="block py-2 hover:text-teal-600 font-medium text-left w-full md:w-auto"
                >
                  Security
                </button>
              </li>
              <li className="pt-2 md:pt-0">
                <button
                  onClick={handleLogout}
                  className="bg-rose-500 text-white px-5 py-2 rounded-lg font-bold hover:bg-rose-600 w-full md:w-auto transition-all"
                >
                  Logout
                </button>
              </li>
            </ul>
          </>
        ) : (
          /* OPTIONAL: Shown when logged out */
          <div className="hidden md:block">
            <span className="text-sm text-gray-400 italic font-medium">
              Please login to continue
            </span>
          </div>
        )}
      </div>

      {/* PASSWORD MODAL */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative text-slate-800">
            <button
              onClick={() => setIsPasswordModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <form onSubmit={handleChangePassword} className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-teal-800">
                Change Password
              </h2>

              {status.error && (
                <p className="mb-4 p-2 bg-red-50 text-red-600 text-sm rounded">
                  {status.error}
                </p>
              )}
              {status.success && (
                <p className="mb-4 p-2 bg-green-50 text-green-600 text-sm rounded">
                  {status.success}
                </p>
              )}

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500">
                    Current Password
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    value={passwordForm.oldPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        oldPassword: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500">
                    New Password
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        newPassword: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        confirmPassword: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-8 bg-teal-700 text-white py-3 rounded-xl font-bold hover:bg-teal-800 disabled:opacity-50"
              >
                {loading ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
}
